import {
  useState, useEffect, useRef
} from 'react';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import {
  Box, Flex, Text, Button
} from '@huspy/briks-web';
import {
  Users, DollarSign, Target, ArrowRight
} from '@huspy/briks-web/icons';
import { useNavigate } from '@tanstack/react-router';
import useAnnouncements from '@modules/announcements/hooks/useAnnouncements';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';

type FeatureBulletPointProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: {
    text: string;
    color: string;
  };
};

const FeatureBulletPoint = ({
  icon, title, description, badge,
}: FeatureBulletPointProps): React.JSX.Element => (
  <Flex gap={ 3 } align='center'>
    <Flex
      w={ 10 }
      h={ 10 }
      borderRadius='2'
      justify='center'
      align='center'
      style={ { background: 'linear-gradient(to bottom, #2F2F2F, #202021)' } }
    >
      {icon}
    </Flex>
    <Flex direction='column'>
      <Flex gap={ 2 } align='center'>
        <Text size='lg' weight='semiBold' color='white'>{title}</Text>
        {badge && (
          <Box border='1px solid transparent' p={ 1 } borderRadius='full' opacity={ 0.8 }>
            <Text size='xs' color={ badge.color }>{badge.text}</Text>
          </Box>
        )}
      </Flex>
      <Text size='md' color='neutral.300'>
        {description}
      </Text>
    </Flex>
  </Flex>
);

type ReferAndEarnLaunchProps = {
  defaultOpen?: boolean;
};

export const ReferAndEarnLaunch = ({ defaultOpen = false }: ReferAndEarnLaunchProps): React.JSX.Element => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
  const [_, markAnnouncementAsSeen] = useAnnouncements('referAndEarn');
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (isOpen && !hasTrackedView.current) {
      trackAmplitudeEvent(USER_EVENTS.REFER_AND_EARN.ANNOUNCEMENT_VIEWED);
      hasTrackedView.current = true;
    }
  }, [isOpen]);

  const handleClose = (): void => {
    markAnnouncementAsSeen();
    setIsOpen(false);
  };

  const handleStartReferring = (): void => {
    trackAmplitudeEvent(USER_EVENTS.REFER_AND_EARN.ANNOUNCEMENT_CTA_CLICKED);
    handleClose();
    navigate({
      to: '/refer-and-earn',
      hash: 'slido-refer-and-earn',
    });
  };

  return (
    <BaseSlido
      dark
      inTopOfAll
      opened={ isOpen }
      close={ handleClose }
      size='md'
      title={ (
        <Flex p={ 4 } direction='column' gap={ 2 }>
          <Text size='md' color='neutral.300'>Refer and Earn</Text>
        </Flex>
      ) }
    >
      <Flex p={ 6 } direction='column' gap={ 6 } h='100%'>
        <Flex direction='column' gap={ 4 }>
          <Text size='6xl' weight='semiBold' color='white'>
            Refer & Earn: send us
            {' '}
            <span style={ { color: '#C6C4C6' } }>real-estate leads</span>
            , earn 30% of agent commission
          </Text>

          <Text size='lg' color='neutral.300'>
            You can now refer buyers/tenants directly from the Broker Portal.
            We&apos;ll route the leads to our real estate agents and keep you in the loop.
          </Text>
        </Flex>

        <Flex direction='column' gap={ 4 }>
          <FeatureBulletPoint
            icon={ <Users width={ 24 } height={ 24 } color='#C6C4C6' /> }
            title='Simple form'
            description='Just name, phone, city, and market type'
          />

          <FeatureBulletPoint
            icon={ <Target width={ 24 } height={ 24 } color='#C6C4C6' /> }
            title='Tracked end-to-end'
            description='Full visibility via Huspy platform'
            badge={ {
              text: 'Comming soon',
              color: '#C6C4C6',
            } }
          />

          <FeatureBulletPoint
            icon={ <DollarSign width={ 24 } height={ 24 } color='#C6C4C6' /> }
            title='Payout on successful deal'
            description='30% of agent commission on every closed transaction'
          />
        </Flex>

        {/* CTA Section */}
        <Flex justify='center' mt={ 8 }>
          <Button
            onClick={ handleStartReferring }
            size='lg'
            style={ {
              background: 'linear-gradient(135deg, #FFFFFF, #F5F5F5)',
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: '600',
              color: '#141413',
              cursor: 'pointer',
              boxShadow: '0 8px 32px rgba(255, 255, 255, 0.3)',
              transition: 'all 0.3s ease',
            } }
            onMouseEnter={ (e) => {
              const target = e.currentTarget;
              target.style.transform = 'translateY(-2px)';
              target.style.boxShadow = '0 12px 40px rgba(255, 255, 255, 0.5)';
              target.style.background = 'linear-gradient(135deg, #F5F5F5, #E8E8E8)';
            } }
            onMouseLeave={ (e) => {
              const target = e.currentTarget;
              target.style.transform = 'translateY(0)';
              target.style.boxShadow = '0 8px 32px rgba(255, 255, 255, 0.3)';
              target.style.background = 'linear-gradient(135deg, #FFFFFF, #F5F5F5)';
            } }
          >
            <Flex align='center' gap={ 2 }>
              <Text size='lg' weight='semiBold' color='#141413'>
                Start Referring
              </Text>
              <ArrowRight width={ 20 } height={ 20 } color='#141413' />
            </Flex>
          </Button>
        </Flex>
      </Flex>
    </BaseSlido>
  );
};
